import { ForwardedRef, forwardRef } from "react";
import Select, { SelectInstance } from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { useLazyQuery } from "@apollo/client";
import useSelectStyles from "utils/useSelectStyles";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import useFetchOptions from "components/Shared/Select/useFetchOptions";

const optionFragment = gql(/* GraphQL */ `
  fragment RetailerRegionSelectOption on RetailerRegion {
    id
    name
  }
`);

const valuesQuery = gql(/* GraphQL */ `
  query RetailerRegionSelectValue($ids: [UUID!]!) {
    retailerRegionsList(filter: { id: { in: $ids } }) {
      ...RetailerRegionSelectOption
    }
  }
`);

const optionsQuery = gql(/* GraphQL */ `
  query RetailerRegionSelectOptions {
    retailerRegionsList(orderBy: NAME_ASC) {
      ...RetailerRegionSelectOption
    }
  }
`);

type Option = DocumentType<typeof optionFragment>;

function getOptions(data: DocumentType<typeof optionsQuery>) {
  return getFragmentData(optionFragment, data?.retailerRegionsList);
}

type Props<IsMulti extends boolean = false> = Omit<
  StateManagerProps<Option, IsMulti>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti>;

function RetailerRegionSelect<IsMulti extends boolean = false>(
  { isMulti, onChange, value: rawValue, isLoading, ...rest }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>();

  const idValueProps = useIdValue<Option, IsMulti, typeof valuesQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.retailerRegionsList),
    isMulti,
    labelKey: "name",
    onChange,
    query: valuesQuery,
    value: rawValue,
  });

  const lazyQuery = useLazyQuery(optionsQuery);

  const fetchOptionsProps = useFetchOptions({
    getOptions,
    lazyQuery,
    menuIsOpen: rest.menuIsOpen,
  });

  return (
    <Select<Option, IsMulti>
      {...styleProps}
      {...idValueProps}
      {...fetchOptionsProps}
      {...{ isMulti, ref }}
      isLoading={
        idValueProps.isLoading || fetchOptionsProps.isLoading || isLoading
      }
      placeholder="Select a region…"
      noOptionsMessage={() => "No regions found"}
      {...rest}
    />
  );
}

export default forwardRef(RetailerRegionSelect);
